// Versiyon: 1.0.0 - İrsaliye Sayfası (Offers.tsx/Inventory.tsx Tasarımı)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Menu,
  MenuItem,
  ButtonGroup,
  CircularProgress,
  Alert,
  TablePagination,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import {
  WAYBILL_FILTERS_EVENT,
  consumePendingWaybillFilters,
  type WaybillFilterRequest,
} from '../../aichat/agent/uiBridge';
import WaybillDisplayModal from './WaybillDisplayModal';
import InvoiceDisplayModal from '../invoice/InvoiceDisplayModal';
import type { InvoiceData } from '../invoice/InvoiceDisplayModal';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { TABLE_STYLES } from '../shared/tableStyles';
import AccountingDataTable from '../shared/AccountingDataTable';
import ProjectBadge from '../shared/ProjectBadge';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../shared/StatsCard';
import TodayIcon from '@mui/icons-material/Today';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

// İrsaliye arayüzü
interface Waybill {
  id: string;
  waybillNumber: string;
  accountId: string;
  accountName: string;
  /** PYS: bağlı olduğu projenin adı (bağ yoksa null). */
  projectName?: string | null;
  accountTaxNumber?: string;
  accountTaxOffice?: string;
  accountAddress?: string;
  accountPhone?: string;
  accountEmail?: string;
  waybillDate: string;
  subtotal: number;
  vatTotal: number;
  grandTotal: number;
  currency: string;
  showPrices: boolean;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
  // Dönüşüm takip alanları
  sourceOrderId?: string | null;
  sourceOrderNumber?: string | null;
  linkedInvoiceId?: string | null;
  linkedInvoiceNumber?: string | null;
  status?: string;
  // Liste ucu (by-company-paged) items dizisi yerine yalnız kalem sayısını döner.
  itemCount?: number;
  items?: Array<{
    id: string;
    stockId?: string;
    itemName: string;
    unit: string;
    quantity: number;
    unitPrice: number;
    vatRate: number;
    netTotal: number;
    grossTotal: number;
  }>;
}

// Modal için export edilecek tip
export interface WaybillData {
  id: string;
  waybillNumber: string;
  accountId: string;
  account: {
    companyName: string;
    taxNumber?: string;
    taxOffice?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  waybillDate: string;
  subtotal: number;
  vatTotal: number;
  grandTotal: number;
  currency: string;
  showPrices: boolean;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
  items?: Array<{
    id: string;
    stockId?: string;
    itemName: string;
    unit: string;
    quantity: number;
    unitPrice: number;
    vatRate: number;
    netTotal: number;
    grossTotal: number;
  }>;
}

const Waybills: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  
  // Filtreleri göster/gizle
  const [showFilters, setShowFilters] = useState(false);
  
  // 3 nokta menüsü için state
  const [anchorElFeatures, setAnchorElFeatures] = useState<null | HTMLElement>(null);
  
  // Sıralama durumu için state
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Arama terimi için state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Açılır menü için state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedWaybillId, setSelectedWaybillId] = useState<string | null>(null);

  // Silme onay modalı için state (silinecek irsaliyenin bilgisi)
  const [deleteConfirmWaybill, setDeleteConfirmWaybill] = useState<{ id: string; waybillNumber: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Veri durumu için state
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [waybills, setWaybills] = useState<Waybill[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Modal için state'ler
  const [isDisplayModalOpen, setIsDisplayModalOpen] = useState(false);
  const [selectedWaybill, setSelectedWaybill] = useState<WaybillData | null>(null);
  
  // Fatura görüntüleme modalı
  const [isInvoiceDisplayModalOpen, setIsInvoiceDisplayModalOpen] = useState(false);
  const [selectedInvoiceForDisplay, setSelectedInvoiceForDisplay] = useState<InvoiceData | null>(null);
  
  // Excel dışa aktarma için loading state'i
  const [loadingExport, setLoadingExport] = useState(false);
  
  // 3 nokta menüsü handler'ları
  const handleOpenFeaturesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFeatures(event.currentTarget);
  };
  
  const handleCloseFeaturesMenu = () => {
    setAnchorElFeatures(null);
  };

  
  // Sayfa değişimi handler
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };
  
  // Sayfa başına satır sayısı değişimi handler
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Sıralama değişikliği için handler
  const handleSortChange = (column: string) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPage(0);
  };
  
  // Menü açma handler'ı
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedWaybillId(id);
  };
  
  // Menü kapatma handler'ı
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedWaybillId(null);
  };

  // Tarihi DD.MM.YYYY formatına dönüştürme
  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    return date.toLocaleDateString(localeCode, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Para formatı
  const formatCurrency = (amount: number, currency: string = 'TRY'): string => {
    return new Intl.NumberFormat(localeCode, {
      style: 'currency', 
      currency: currency 
    }).format(amount);
  };

  // Şirket ID'sini ve verileri yükleme
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) {
          setError('Kullanıcı oturumu bulunamadı.');
          setLoading(false);
          return;
        }
        
        const userData = await firebaseDataService.getUserData(uid);
        
        if (userData && userData.companyId) {
          const userCompanyId = userData.companyId;
          setCompanyId(userCompanyId);
          // loadWaybills tetiklenecek companyId state değişince useEffect üzerinden
        } else {
          setError('Kullanıcı bilgileri bulunamadı.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Veri yükleme hatası:', err);
        setError('Veriler yüklenirken bir hata oluştu.');
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, []);
  
  // İrsaliyeleri yükleme (SQL'den - server-side pagination)
  const loadWaybills = useCallback(async (compId: string) => {
    console.log("📤 [SQL] İrsaliyeleri yükleniyor...", compId);
    setLoading(true);
    
    try {
      const response = await accountingApi.get(ACCOUNTING_API.WAYBILL_BY_COMPANY_PAGED({
        page: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm || undefined,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
      }));
      
      const result = response.data;
      console.log("📥 [SQL] İrsaliyeler yanıtı:", result);
      
      if (result.success && result.data) {
        const waybillsList: Waybill[] = result.data.map((waybill: any) => ({
          id: waybill.id,
          waybillNumber: waybill.waybillNumber || '',
          accountId: waybill.accountId?.toString() || '',
          accountName: waybill.accountName || tt('İsimsiz Müşteri', 'Unnamed Customer'),
          projectName: waybill.projectName || null,
          waybillDate: waybill.waybillDate ? waybill.waybillDate.split('T')[0] : '',
          grandTotal: waybill.grandTotal || 0,
          currency: waybill.currency || 'TRY',
          showPrices: waybill.showPrices ?? true,
          createdAt: waybill.createdAt || '',
          itemCount: waybill.itemCount || 0,
          // ✅ Dönüşüm takip alanları
          sourceOrderId: waybill.sourceOrderId || null,
          sourceOrderNumber: waybill.sourceOrderNumber || null,
          linkedInvoiceId: waybill.linkedInvoiceId || null,
          linkedInvoiceNumber: waybill.linkedInvoiceNumber || null,
          status: waybill.status || 'active',
        }));
        
        console.log("✅ [SQL] İşlenmiş irsaliyeler:", waybillsList);
        setWaybills(waybillsList);
        setTotalCount(result.totalCount || 0);
      } else {
        console.log("⚠️ [SQL] İrsaliyeler bulunamadı");
        setWaybills([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('❌ [SQL] İrsaliyeler yüklenirken hata:', error);
      setWaybills([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, sortColumn, sortDirection]);

  // Filtrelenmiş irsaliyeler - server-side olduğu için direkt waybills kullanılır
  const filteredWaybills = waybills;

  // İstatistik kartları - filtrelenmiş verilere göre hesaplanır
  const waybillStats = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const todayWaybills = filteredWaybills.filter(w => new Date(w.waybillDate || w.createdAt) >= todayStart);
    const todayCount = todayWaybills.length;
    const todayTotal = todayWaybills.reduce((sum, w) => sum + w.grandTotal, 0);

    const thisMonthWaybills = filteredWaybills.filter(w => new Date(w.waybillDate || w.createdAt) >= thisMonthStart);
    const monthCount = thisMonthWaybills.length;
    const monthTotal = thisMonthWaybills.reduce((sum, w) => sum + w.grandTotal, 0);

    const totalItems = filteredWaybills.reduce((sum, w) => sum + (w.items?.length || w.itemCount || 0), 0);

    return { todayCount, todayTotal, monthCount, monthTotal, totalCount, totalItems };
  }, [filteredWaybills, totalCount]);

  // Arama/filtre yapıldığında sayfayı sıfırla
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // AI ajanı: sayfa arama filtresini doldur
  useEffect(() => {
    const applyAgentFilters = (req: WaybillFilterRequest) => {
      if (!req) return;
      if (req.clearAll) setSearchTerm('');
      if (req.searchText !== undefined) setSearchTerm(req.searchText);
      setPage(0);
    };
    const pending = consumePendingWaybillFilters();
    if (pending) applyAgentFilters(pending);
    const handler = (e: Event) => {
      consumePendingWaybillFilters();
      applyAgentFilters((e as CustomEvent<WaybillFilterRequest>).detail);
    };
    window.addEventListener(WAYBILL_FILTERS_EVENT, handler);
    return () => window.removeEventListener(WAYBILL_FILTERS_EVENT, handler);
  }, []);

  // Server-side veri yükleme
  useEffect(() => {
    if (companyId) {
      const timer = setTimeout(() => {
        loadWaybills(companyId);
      }, searchTerm ? 400 : 0);
      return () => clearTimeout(timer);
    }
  }, [companyId, page, rowsPerPage, searchTerm, sortColumn, sortDirection, loadWaybills]);

  // Sayfalanmış irsaliyeler (mobil kart görünümü için)
  const paginatedWaybills = filteredWaybills;

  // AccountingDataTable kolon tanımları
  const waybillColumns = useMemo<DataTableColumn<Waybill>[]>(() => [
    {
      id: 'waybillNumber',
      label: tt('İrsaliye No', 'Waybill No'),
      field: 'waybillNumber',
      width: '12%',
      sortable: true,
    },
    {
      id: 'accountName',
      label: tt('Müşteri', 'Customer'),
      field: 'accountName',
      width: '25%',
      sortable: true,
      tooltip: (row) => row.accountName,
      // Proje bağı cari adının altında küçük etiket olarak gösterilir (PYS)
      render: (row) => (
        <Box>
          <Typography variant="body2" noWrap>{row.accountName}</Typography>
          <ProjectBadge projectName={row.projectName} />
        </Box>
      ),
    },
    {
      id: 'waybillDate',
      label: tt('Tarih', 'Date'),
      width: '12%',
      sortable: true,
      field: 'waybillDate',
      render: (row) => formatDate(row.waybillDate),
    },
    {
      id: 'grandTotal',
      label: tt('Tutar', 'Amount'),
      width: '15%',
      sortable: true,
      field: 'grandTotal',
      // Banka/kasa görünümüyle tam uyumlu olması için Typography kullanıldı.
      // Sadece sunum değiştirildi; yapı ve tıklanabilirlik korunmuştur.
      render: (row) => (
        <Typography
          variant="body2"
          fontWeight="bold"
          color="success.main"
          sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
        >
          {row.grandTotal.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
          {row.currency === 'USD' ? '$' : row.currency === 'EUR' ? '€' : '₺'}
        </Typography>
      ),
    },
    {
      id: 'showPrices',
      label: tt('Fiyat', 'Price'),
      width: '10%',
      render: (row) => (
        <Chip
          icon={row.showPrices ? undefined : <VisibilityOffIcon fontSize="small" />}
          label={row.showPrices ? tt('Görünür', 'Visible') : tt('Gizli', 'Hidden')}
          size="small"
          color={row.showPrices ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      id: 'notes',
      label: tt('Notlar', 'Notes'),
      field: 'notes',
      width: '13%',
      tooltip: (row) => row.notes || '-',
      render: (row) => row.notes ? (row.notes.length > 20 ? row.notes.substring(0, 20) + '...' : row.notes) : '-',
    },
    {
      id: 'status',
      label: tt('Durum', 'Status'),
      width: '10%',
      render: (row: any) => (
        row.status === 'invoiced' && row.linkedInvoiceId ? (
          // Siparişler sayfasıyla aynı stil: dolu chip, tüm durumlar yatayda eşit genişlik, ortaya hizalı
          <Chip
            label={tt('Faturalandı', 'Invoiced')}
            size="small"
            color="info"
            sx={{ cursor: 'default', minWidth: '110px', justifyContent: 'center' }}
            title={`${tt('Fatura', 'Invoice')}: ${row.linkedInvoiceNumber || ''}`}
          />
        ) : row.sourceOrderId ? (
          <Chip
            label={tt('Aktif', 'Active')}
            size="small"
            color="success"
            sx={{ cursor: 'default', minWidth: '110px', justifyContent: 'center' }}
            title={`${tt('Sipariş', 'Order')}: ${row.sourceOrderNumber || ''}`}
          />
        ) : (
          <Chip
            label={tt('Aktif', 'Active')}
            size="small"
            color="success"
            sx={{ minWidth: '110px', justifyContent: 'center' }}
          />
        )
      ),
    },
    {
      id: 'actions',
      label: tt('İşlemler', 'Actions'),
      width: '8%',
      align: 'center' as const,
      render: (row) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleMenuOpen(e, row.id);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ], [isTr, localeCode]);
  
  // Sil'e tıklanınca önce onay modalını aç
  const handleDeleteClick = () => {
    if (!selectedWaybillId) {
      handleMenuClose();
      return;
    }
    const waybill = waybills.find(w => w.id === selectedWaybillId);
    setDeleteConfirmWaybill({
      id: selectedWaybillId,
      waybillNumber: waybill?.waybillNumber || ''
    });
    handleMenuClose();
  };

  // İrsaliye silme (SQL'den) — onay modalından çağrılır
  const handleDeleteWaybill = async () => {
    if (!deleteConfirmWaybill || !companyId) {
      setDeleteConfirmWaybill(null);
      return;
    }
    
    try {
      setIsDeleting(true);
      
      const deleteDto = {
        Id: deleteConfirmWaybill.id,
        CompanyId: companyId,
        DeletedBy: ownAuthSession.getCurrentEmail() || 'System'
      };
      
      console.log('📤 [SQL] İrsaliye siliniyor:', deleteDto);
      
      const response = await accountingApi.post(ACCOUNTING_API.WAYBILL_DELETE, deleteDto);
      
      const result = response.data;
      console.log('📥 [SQL] Silme yanıtı:', result);
      
      if (result.success) {
        console.log(`✅ İrsaliye başarıyla silindi`);
        // Reload from server to get correct totalCount
        loadWaybills(companyId);
      } else {
        throw new Error(result.message || 'İrsaliye silinemedi');
      }
      
      setDeleteConfirmWaybill(null);
    } catch (err: any) {
      console.error('❌ [SQL] İrsaliye silme hatası:', err);
      setError(err.message || 'İrsaliye silinirken bir hata oluştu.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Düzenleme sayfasına yönlendirme
  const handleEditWaybill = (waybillId: string) => {
    navigate(`/irsaliye/duzenle/${waybillId}`);
  };

  // Görüntüleme - API'den detaylı irsaliye bilgisi çeker
  const handleViewWaybill = async (waybillId: string) => {
    if (!companyId) {
      setError('Şirket bilgisi bulunamadı.');
      return;
    }
    
    try {
      setLoading(true);
      console.log("📤 [SQL] İrsaliye detayı yükleniyor:", waybillId);
      
      const response = await accountingApi.get(ACCOUNTING_API.WAYBILL_BY_ID(waybillId));
      
      const result = response.data;
      console.log("📥 [SQL] İrsaliye detayı yanıtı:", result);
      
      if (result.success && result.data) {
        const waybillData = result.data;
        const formattedWaybill: WaybillData = {
          id: waybillData.id,
          waybillNumber: waybillData.waybillNumber || '',
          accountId: waybillData.accountId?.toString() || '',
          account: {
            companyName: waybillData.accountName || tt('İsimsiz Müşteri', 'Unnamed Customer'),
            taxNumber: waybillData.accountTaxNumber || '',
            taxOffice: waybillData.accountTaxOffice || '',
            address: waybillData.accountAddress || '',
            phone: waybillData.accountPhone || '',
            email: waybillData.accountEmail || '',
          },
          waybillDate: waybillData.waybillDate || '',
          subtotal: waybillData.subtotal || 0,
          vatTotal: waybillData.vatTotal || 0,
          grandTotal: waybillData.grandTotal || 0,
          currency: waybillData.currency || 'TRY',
          showPrices: waybillData.showPrices ?? true,
          notes: waybillData.notes || '',
          createdAt: waybillData.createdAt || '',
          updatedAt: waybillData.updatedAt || '',
          items: waybillData.items?.map((item: any) => ({
            id: item.id,
            stockId: item.stockId,
            itemName: item.itemName,
            unit: item.unit,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            vatRate: item.vatRate,
            netTotal: item.netTotal || 0,
            grossTotal: item.grossTotal || 0
          })) || []
        };
        
        console.log("✅ Görüntülenecek irsaliye:", formattedWaybill);
        setSelectedWaybill(formattedWaybill);
        setIsDisplayModalOpen(true);
      } else {
        throw new Error(result.message || 'İrsaliye detayı yüklenemedi');
      }
    } catch (err: any) {
      console.error('❌ İrsaliye detayı yüklenirken hata:', err);
      setError(err.message || 'İrsaliye detayı yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // PDF indirme işlemi
  const handleDownloadPdf = () => {
    if (!selectedWaybillId || !companyId) {
      handleMenuClose();
      return;
    }

    const waybill = waybills.find(w => w.id === selectedWaybillId);
    if (waybill) {
      const formattedWaybill: WaybillData = {
        id: waybill.id,
        waybillNumber: waybill.waybillNumber,
        accountId: waybill.accountId,
        account: {
          companyName: waybill.accountName,
          taxNumber: waybill.accountTaxNumber,
          taxOffice: waybill.accountTaxOffice,
          address: waybill.accountAddress,
          phone: waybill.accountPhone,
          email: waybill.accountEmail,
        },
        waybillDate: waybill.waybillDate,
        subtotal: waybill.subtotal,
        vatTotal: waybill.vatTotal,
        grandTotal: waybill.grandTotal,
        currency: waybill.currency,
        showPrices: waybill.showPrices,
        notes: waybill.notes,
        createdAt: waybill.createdAt,
        updatedAt: waybill.updatedAt,
        items: waybill.items
      };
      setSelectedWaybill(formattedWaybill);
      setIsDisplayModalOpen(true);
    }
    
    handleMenuClose();
  };

  // Modal kapatma fonksiyonu
  const handleCloseDisplayModal = () => {
    setIsDisplayModalOpen(false);
    setSelectedWaybill(null);
  };

  // ✅ İrsaliyeyi faturaya dönüştürme - localStorage + navigate pattern
  const handleConvertToInvoice = async () => {
    if (!selectedWaybillId || !companyId) {
      handleMenuClose();
      return;
    }
    
    const currentWaybillId = selectedWaybillId;
    handleMenuClose();
    
    try {
      setLoading(true);
      
      console.log('📤 [API] İrsaliye detayları çekiliyor (fatura için):', currentWaybillId);
      const response = await accountingApi.get(ACCOUNTING_API.WAYBILL_BY_ID(currentWaybillId));
      
      const result = response.data;
      if (!result.success || !result.data) {
        throw new Error(result.message || 'İrsaliye detayları yüklenemedi');
      }
      
      const waybillData = result.data;
      
      const waybillDataForInvoice = {
        waybillId: waybillData.id,
        waybillNumber: waybillData.waybillNumber,
        waybillDate: waybillData.waybillDate ? waybillData.waybillDate.split('T')[0] : '',
        accountId: waybillData.accountId?.toString() || '',
        account: {
          companyName: waybillData.accountName || '',
          taxNumber: waybillData.accountTaxNumber || '',
          taxOffice: waybillData.accountTaxOffice || '',
          address: waybillData.accountAddress || '',
          phone: waybillData.accountPhone || '',
          email: waybillData.accountEmail || '',
        },
        items: waybillData.items?.map((item: any) => ({
          id: item.id,
          itemId: item.stockId || item.id,
          stockId: item.stockId || item.id,
          name: item.itemName,
          itemName: item.itemName,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          priceType: 'VAT_EXCLUDED',
          enteredPrice: item.unitPrice,
          vatRate: item.vatRate || 0,
          sctRate: 0,
          discountRate: 0,
          netTotal: item.netTotal || 0,
          vatTotal: item.grossTotal - item.netTotal || 0,
          vatAmount: item.grossTotal - item.netTotal || 0,
          grossTotal: item.grossTotal || 0,
          warehouseId: '',
        })) || [],
        notes: waybillData.notes ? `${tt('İrsaliye No', 'Waybill No')}: ${waybillData.waybillNumber} - ${waybillData.notes}` : `${tt('İrsaliye No', 'Waybill No')}: ${waybillData.waybillNumber}`,
        currency: waybillData.currency || 'TRY',
      };
      
      console.log('💾 localStorage\'a kaydedilen veri (fatura):', waybillDataForInvoice);
      localStorage.setItem('waybillToInvoice', JSON.stringify(waybillDataForInvoice));
      
      navigate('/fatura/satis/yeni?fromWaybill=true');
    } catch (err: any) {
      console.error('❌ İrsaliye detayları yüklenirken hata:', err);
      setError(err.message || 'İrsaliye detayları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      const workbook = new ExcelJS.Workbook();
      
      // İrsaliyeler sayfası
      const waybillsSheet = workbook.addWorksheet(tt('İrsaliyeler', 'Waybills'));
      
      // Başlık satırı
      waybillsSheet.mergeCells('A1:G1');
      const titleCell = waybillsSheet.getCell('A1');
      titleCell.value = tt('İrsaliye Listesi', 'Waybill List');
      titleCell.style = {
        font: { bold: true, size: 14, color: { argb: 'FF000000' } },
        alignment: { horizontal: 'center' }
      };
      
      waybillsSheet.addRow([]);
      
      // Tablo başlıkları
      const headerRow = waybillsSheet.addRow([
        tt('İrsaliye No', 'Waybill No'),
        tt('Müşteri', 'Customer'),
        tt('Tarih', 'Date'),
        tt('Para Birimi', 'Currency'),
        tt('Tutar', 'Amount'),
        tt('Fiyat Görünür', 'Prices Visible'),
        tt('Açıklama', 'Description')
      ]);
      
      headerRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
          border: {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          }
        };
      });
      
      // İrsaliye verilerini ekle
      waybills.forEach((waybill) => {
        waybillsSheet.addRow([
          waybill.waybillNumber,
          waybill.accountName,
          formatDate(waybill.waybillDate),
          waybill.currency,
          waybill.grandTotal,
          waybill.showPrices ? tt('Evet', 'Yes') : tt('Hayır', 'No'),
          waybill.notes || '-'
        ]);
      });
      
      // Sütun genişlikleri
      waybillsSheet.getColumn(1).width = 15;
      waybillsSheet.getColumn(2).width = 30;
      waybillsSheet.getColumn(3).width = 15;
      waybillsSheet.getColumn(4).width = 10;
      waybillsSheet.getColumn(5).width = 15;
      waybillsSheet.getColumn(6).width = 12;
      waybillsSheet.getColumn(7).width = 30;
      
      waybillsSheet.getColumn(5).numFmt = '#,##0.00';
      
      // Zebrastripe
      for (let i = 4; i <= waybillsSheet.rowCount; i++) {
        const fillColor = i % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
        waybillsSheet.getRow(i).eachCell(cell => {
          cell.style = {
            ...cell.style,
            fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
            border: {
              top: { style: 'thin' as const },
              left: { style: 'thin' as const },
              bottom: { style: 'thin' as const },
              right: { style: 'thin' as const }
            }
          };
        });
      }
      
      // Dosya adı
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${tt('Irsaliyeler_Listesi', 'Waybills_List')}_${currentDate}.xlsx`;
      
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      console.log('Excel dışa aktarma tamamlandı.');
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError(tt('Excel dışa aktarma sırasında bir hata oluştu.', 'An error occurred during Excel export.'));
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <>
      {/* Üst Başlık */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <LocalShippingIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {tt('İrsaliyeler', 'Waybills')}
          </Typography>
          <GuideButton guideKey="documents.waybills" sx={{ ml: 0.5 }} />
        </Box>
      </Box>

      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={loading && waybills.length === 0}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<TodayIcon />}
            color={STATS_COLORS.blue}
            title={`${waybillStats.todayCount} ${tt('İrsaliye', 'Waybill')}`}
            subtitle={`${tt('Bugün Kesilen', 'Issued Today')} (${formatStatsCurrency(waybillStats.todayTotal)})`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CalendarMonthIcon />}
            color={STATS_COLORS.green}
            title={`${waybillStats.monthCount} ${tt('İrsaliye', 'Waybill')}`}
            subtitle={`${tt('Bu Ay', 'This Month')} (${formatStatsCurrency(waybillStats.monthTotal)})`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<LocalShippingIcon />}
            color={STATS_COLORS.purple}
            title={`${waybillStats.totalCount}`}
            subtitle={tt('Toplam İrsaliye', 'Total Waybills')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<ReceiptIcon />}
            color={STATS_COLORS.orange}
            title={`${waybillStats.totalItems}`}
            subtitle={tt('Toplam Kalem Sayısı', 'Total Item Count')}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Arama/Filtre ve Butonlar - Aynı Satır */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Arama Alanı */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={tt('İrsaliye Ara...', 'Search Waybill...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Filtreleri Göster İkonu */}
          <Grid item xs={12} md={0.5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <Tooltip title={showFilters ? tt('Filtreleri Gizle', 'Hide Filters') : tt('Filtreleri Göster', 'Show Filters')}>
              <IconButton
                color={showFilters ? "primary" : "default"}
                onClick={() => setShowFilters(!showFilters)}
                size="small"
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Grid>

          {/* Desktop Butonlar - Aynı Satır */}
          <Grid item xs={12} md={8.5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <ButtonGroup>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/irsaliye/yeni')}
              >
                {tt('Yeni İrsaliye', 'New Waybill')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleOpenFeaturesMenu}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <MoreVertIcon />
              </Button>
            </ButtonGroup>
          </Grid>

          {/* Mobil Butonlar */}
          <Grid item xs={12} sx={{ display: { xs: 'grid', md: 'none' } }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sx={{ mb: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color={showFilters ? "primary" : "inherit"}
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  size="small"
                >
                  {showFilters ? tt('Filtreleri Gizle', 'Hide Filters') : tt('Filtreleri Göster', 'Show Filters')}
                </Button>
              </Grid>
              <Grid item xs={8}>
                <Button 
                  fullWidth
                  size="small"
                  variant="contained" 
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/irsaliye/yeni')}
                >
                  {tt('Yeni İrsaliye', 'New Waybill')}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button 
                  fullWidth
                  size="small"
                  variant="outlined"
                  onClick={handleOpenFeaturesMenu}
                  sx={{ minWidth: 'auto' }}
                >
                  <MoreVertIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Filtreler - showFilters true ise göster */}
      {showFilters && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Sıralama */}
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                onClick={() => handleSortChange('createdAt')}
              >
                {sortDirection === 'asc' ? tt('Eskiden Yeniye', 'Oldest to Newest') : tt('Yeniden Eskiye', 'Newest to Oldest')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Ana İçerik */}
      <Box sx={{ mb: 3 }}>
        {/* Tablo */}
        <Box sx={{ position: 'relative' }}>
          {/* Desktop: AccountingDataTable */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <AccountingDataTable<Waybill>
              columns={waybillColumns}
              rows={filteredWaybills}
              getRowId={(row) => row.id}
              loading={loading}
              serverSidePagination={true}
              totalCount={totalCount}
              page={page}
              pageSize={rowsPerPage}
              onPageChange={(newPage, newPageSize) => {
                if (newPageSize !== rowsPerPage) {
                  setRowsPerPage(newPageSize);
                  setPage(0);
                } else {
                  setPage(newPage);
                }
              }}
              onSort={handleSortChange}
              sortField={sortColumn}
              sortDirection={sortDirection}
              emptyMessage={tt('Henüz irsaliye kaydı bulunamadı.', 'No waybill records found yet.')}
              emptyIcon={<LocalShippingIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
              ariaLabel={tt('irsaliyeler tablosu', 'waybills table')}
              columnResizeStorageKey="waybills"
            />
          </Box>

          {/* Mobile: Card View */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
            {paginatedWaybills.length === 0 ? (
              <Box sx={{ 
                p: 4, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
                borderRadius: 2
              }}>
                <LocalShippingIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {tt('Henüz irsaliye kaydı bulunamadı.', 'No waybill records found yet.')}
                </Typography>
              </Box>
            ) : (
              paginatedWaybills.map((waybill) => (
                <Card 
                  key={waybill.id}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'primary.light',
                    bgcolor: 'rgba(25, 118, 210, 0.04)'
                  }}
                >
                  <CardContent>
                    {/* Başlık - İrsaliye No */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalShippingIcon color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {waybill.waybillNumber}
                        </Typography>
                      </Box>
                      <Chip 
                        icon={waybill.showPrices ? undefined : <VisibilityOffIcon fontSize="small" />}
                        label={waybill.showPrices ? tt('Fiyat Görünür', 'Prices Visible') : tt('Fiyat Gizli', 'Prices Hidden')} 
                        size="small"
                        color={waybill.showPrices ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                    
                    {/* Müşteri */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {tt('Müşteri:', 'Customer:')}
                      </Typography>
                      <Typography variant="body2">
                        {waybill.accountName}
                      </Typography>
                    </Box>
                    
                    {/* Tarih */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {tt('Tarih:', 'Date:')}
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(waybill.waybillDate)}
                      </Typography>
                    </Box>
                    
                    {/* Tutar */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 2, 
                      pt: 1.5, 
                      borderTop: '1px solid', 
                      borderColor: 'divider' 
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        {tt('Tutar:', 'Amount:')}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                        {formatCurrency(waybill.grandTotal, waybill.currency)}
                      </Typography>
                    </Box>
                    
                    {/* Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewWaybill(waybill.id)}
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditWaybill(waybill.id)}
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={(e) => handleMenuOpen(e, waybill.id)}
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
            
            {/* Mobile Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={TABLE_STYLES.rowsPerPageOptions}
              labelRowsPerPage={tt('Sayfa başına satır:', 'Rows per page:')}
              labelDisplayedRows={({ from, to, count }) => (isTr ? `${count} kayıttan ${from}-${to} arası gösteriliyor` : `${from}-${to} of ${count} records`)}
              sx={TABLE_STYLES.pagination}
            />
          </Box>
        </Box>
      </Box>
      
      {/* 3 Nokta (Özellikler) Menüsü */}
      <Menu
        anchorEl={anchorElFeatures}
        open={Boolean(anchorElFeatures)}
        onClose={handleCloseFeaturesMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          onClick={() => {
            exportToExcel();
            handleCloseFeaturesMenu();
          }}
          disabled={loadingExport || loading || waybills.length === 0}
        >
          {loadingExport ? <CircularProgress size={16} sx={{ mr: 1 }} /> : <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />}
          {tt('Excel İndir', 'Download Excel')}
        </MenuItem>
      </Menu>
      
      {/* İşlemler Menüsü */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          handleViewWaybill(selectedWaybillId!);
          handleMenuClose();
        }}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Görüntüle', 'View')}
        </MenuItem>
        <MenuItem onClick={() => {
          handleEditWaybill(selectedWaybillId!);
          handleMenuClose();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Düzenle', 'Edit')}
        </MenuItem>
        <MenuItem onClick={handleDownloadPdf}>
          <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('PDF İndir', 'Download PDF')}
        </MenuItem>
        {/* Fatura: Göster veya Oluştur */}
        {(() => {
          const selected = waybills.find((w: any) => w.id === selectedWaybillId);
          if (!selected) return null;
          if ((selected as any).linkedInvoiceId) {
            const invoiceId = (selected as any).linkedInvoiceId;
            return (
              <MenuItem onClick={async () => {
                handleMenuClose();
                if (!companyId || !invoiceId) return;
                try {
                  setLoading(true);
                  const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(invoiceId));
                  const result = response.data;
                  if (result.success && result.data) {
                    const inv = result.data;
                    const formattedInvoice: InvoiceData = {
                      id: inv.id,
                      invoiceNumber: inv.invoiceNumber,
                      account: {
                        companyName: inv.accountName || '',
                        taxNumber: inv.accountTaxNumber || '',
                        taxOffice: inv.accountTaxOffice || '',
                        address: inv.accountAddress || '',
                        phone: inv.accountPhone || '',
                        email: inv.accountEmail || '',
                      },
                      date: inv.invoiceDate || inv.date,
                      dueDate: inv.dueDate,
                      items: inv.items?.map((item: any) => ({
                        itemName: item.itemName || item.name,
                        quantity: item.quantity,
                        unit: item.unit,
                        unitPrice: item.unitPrice,
                        vatRate: item.vatRate || 0,
                        sctRate: item.sctRate || 0,
                        discountRate: item.discountRate || 0,
                        lineTotal: item.netTotal || (item.quantity * item.unitPrice),
                        vatAmount: item.vatAmount || item.vatTotal || 0,
                        sctAmount: item.sctAmount || 0,
                        grossTotal: item.grossTotal || 0,
                      })) || [],
                      notes: inv.notes,
                      subtotal: inv.subtotal || 0,
                      vatTotal: inv.vatTotal || 0,
                      sctTotal: inv.sctTotal || 0,
                      discountTotal: inv.discountTotal || 0,
                      grandTotal: inv.grandTotal || 0,
                      currency: inv.currency || 'TRY',
                      type: 'sales',
                      sourceOfferId: inv.sourceOfferId,
                      sourceOfferNumber: inv.sourceOfferNumber,
                    };
                    setSelectedInvoiceForDisplay(formattedInvoice);
                    setIsInvoiceDisplayModalOpen(true);
                  }
                } catch (err) {
                  console.error('Fatura detayı yüklenemedi:', err);
                } finally {
                  setLoading(false);
                }
              }}>
                <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
                {tt('Fatura Göster', 'View Invoice')} {(selected as any).linkedInvoiceNumber ? `(${(selected as any).linkedInvoiceNumber})` : ''}
              </MenuItem>
            );
          }
          return (
            <MenuItem onClick={handleConvertToInvoice}>
              <ReceiptIcon fontSize="small" sx={{ mr: 1 }} />
              {tt('Fatura Oluştur', 'Create Invoice')}
            </MenuItem>
          );
        })()}
        <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Sil', 'Delete')}
        </MenuItem>
      </Menu>

      {/* İrsaliye Silme Onay Modalı */}
      <Dialog
        open={!!deleteConfirmWaybill}
        onClose={isDeleting ? undefined : () => setDeleteConfirmWaybill(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{tt('İrsaliyeyi Sil', 'Delete Waybill')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteConfirmWaybill?.waybillNumber ? (
              <>
                <strong>{deleteConfirmWaybill.waybillNumber}</strong>{' '}
                {tt('numaralı irsaliyeyi silmek istediğinize emin misiniz?', 'Are you sure you want to delete this waybill?')}
              </>
            ) : (
              tt('Bu irsaliyeyi silmek istediğinize emin misiniz?', 'Are you sure you want to delete this waybill?')
            )}{' '}
            {tt('Bu işlem geri alınamaz.', 'This action cannot be undone.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmWaybill(null)}
            color="inherit"
            disabled={isDeleting}
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button
            onClick={handleDeleteWaybill}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? tt('Siliniyor...', 'Deleting...') : tt('Sil', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* İrsaliye Görüntüleme Modalı */}
      {selectedWaybill && (
        <WaybillDisplayModal
          open={isDisplayModalOpen}
          onClose={handleCloseDisplayModal}
          waybill={selectedWaybill}
          companyId={companyId}
        />
      )}
      
      {/* Fatura Görüntüleme Modalı */}
      <InvoiceDisplayModal
        open={isInvoiceDisplayModalOpen}
        onClose={() => {
          setIsInvoiceDisplayModalOpen(false);
          setSelectedInvoiceForDisplay(null);
        }}
        invoice={selectedInvoiceForDisplay}
        companyId={companyId}
      />
    </>
  );
};

export default Waybills;
